import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import TierCard from 'components/Profile/TierCard';
import { Tier } from 'dragonmouse-shared/interfaces';
import { useState, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { selectUser } from 'redux/db/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { logout } from 'utils';
import { displayPromptModal } from 'redux/mode/util';
import Icon, { IconType } from 'components/Icon';
import { useTheme } from 'styled-components';
import { toast } from 'react-toastify';
import forge from 'apis/forge';
import * as S from './styles';

const formatDate = (date: Date | string): string => {
	try {
		const dateObj = typeof date === 'string' ? new Date(date) : date;
		if (isNaN(dateObj.getTime())) {
			console.error('Invalid date value', date);
			return '';
		}
		return new Intl.DateTimeFormat('en-US', {
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		}).format(dateObj);
	} catch (error) {
		console.error('Could not format date', date);
		return '';
	}
};

const Profile = () => {
	const theme = useTheme();
	const { authUser, user, subscription, isMobile } = useSelector(
		(r: RootReducer) => ({
			authUser: r.mode.authUser,
			user: selectUser(r.db),
			subscription: r.db.subscription,
			isMobile: selectIsMobile(r.mode),
		}),
		shallowEqual
	);
	// Initialize isYearly based on subscription interval for existing subscribers,
	// Default to true (yearly) for free users (who can toggle it)
	const [isYearly, setIsYearly] = useState(() => {
		return subscription ? subscription.interval === 'year' : true;
	});

	const displayName = user?.displayName || '';
	const currentTier = subscription?.tier ?? Tier.FREE;
	const hasActiveSubscription = currentTier !== Tier.FREE; // && subscription?.stripeSubscriptionId; <-- this should always be true. not sure how the FE should handle the case when it isn't.

	const footerContentRef = useRef<HTMLDivElement>(null);
	const [footerWidth, setFooterWidth] = useState(0);

	// Keep isYearly in sync with subscription interval for existing subscribers
	useEffect(() => {
		if (hasActiveSubscription && subscription?.interval) {
			setIsYearly(subscription.interval === 'year');
		}
	}, [hasActiveSubscription, subscription?.interval]);

	// Track footer content width with ResizeObserver
	useEffect(() => {
		const element = footerContentRef.current;
		if (!element) return;

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setFooterWidth(entry.contentRect.width);
			}
		});

		resizeObserver.observe(element);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const handleChangeDisplayName = () => {
		displayPromptModal({
			title: 'Change Display Name',
			submitLabel: 'Save Changes',
			placeholder: 'Enter display name',
			initialValue: displayName,
			validate: (value) => {
				const trimmed = value.trim();
				if (!trimmed) {
					return 'Display name cannot be empty';
				}
				if (trimmed.length > 50) {
					return 'Display name must be 50 characters or less';
				}
				return null;
			},
			onSubmit: (newDisplayName) => {
				updateDisplayName(newDisplayName || displayName);
			},
		});
	};

	const updateDisplayName = async (newDisplayName: string) => {
		if (!user?.id) {
			toast.warning('Could not update display name due to internal error. Please contact support.');
			return;
		}

		await forge.updateUser(
			{
				update: {
					id: user.id,
					displayName: newDisplayName.trim(),
				},
			},
			true // showToast
		);
	};

	const renewalDate = subscription?.currentPeriodEnd
		? formatDate(subscription.currentPeriodEnd)
		: '';

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderTierCards = () => {
		switch (currentTier) {
			case Tier.FREE:
				return (
					<>
						<TierCard variant="upgrade" tier={Tier.TIER_1} isYearly={isYearly} />
						<TierCard variant="upgrade" tier={Tier.TIER_2} isYearly={isYearly} />
					</>
				);
			case Tier.TIER_1: // PRO
				return (
					<>
						<TierCard tier={Tier.TIER_1} variant="current" renewalDate={renewalDate} />
						<TierCard tier={Tier.TIER_2} variant="upgradeToMax" isYearly={isYearly} />
					</>
				);
			case Tier.TIER_2: // MAX
				return (
					<>
						<TierCard tier={Tier.TIER_2} variant="current" renewalDate={renewalDate} />
					</>
				);
			default:
				return null;
		}
	};

	const headline = hasActiveSubscription ? 'Your Subscription' : 'Upgrade Your Experience';
	const subtitle =
		currentTier === Tier.FREE
			? 'Choose the plan that works for you'
			: currentTier === Tier.TIER_1
			? 'Manage your Pro account'
			: 'Manage your Max account';

	return (
		<S.Base>
			<S.Container>
				{isMobile && (
					<S.SidebarToggle>
						<SidebarToggle />
					</S.SidebarToggle>
				)}
				<S.SubscriptionSection>
					<S.SectionHeadline>{headline}</S.SectionHeadline>
					<S.SectionSubheading>{subtitle}</S.SectionSubheading>
					{!hasActiveSubscription && (
						<S.PricingToggleWrapper>
							<S.PricingToggle>
								<S.ToggleIndicator isYearly={isYearly} />
								<S.ToggleOption active={!isYearly} onClick={() => setIsYearly(false)}>
									Monthly
								</S.ToggleOption>
								<S.ToggleOption active={isYearly} onClick={() => setIsYearly(true)}>
									Yearly
								</S.ToggleOption>
							</S.PricingToggle>
						</S.PricingToggleWrapper>
					)}
					<S.TierCards>{renderTierCards()}</S.TierCards>
					<S.PricingLinkWrapper>
						<S.PricingLink
							href="https://moonchild.ai/pricing"
							target="_blank"
							rel="noopener noreferrer"
						>
							View detailed pricing →
						</S.PricingLink>
					</S.PricingLinkWrapper>
				</S.SubscriptionSection>
			</S.Container>
			<S.AccountFooter>
				<S.AccountFooterContent ref={footerContentRef}>
					{!isMobile && (
						<S.DisplayNameEdit
							onClick={handleChangeDisplayName}
							hide={footerWidth > 0 && footerWidth <= 400}
						>
							<S.DisplayNameText>{displayName}</S.DisplayNameText>
							<S.EditIcon>
								<Icon type={IconType.EDIT} width={16} height={16} color={theme.blue[500]} />
							</S.EditIcon>
						</S.DisplayNameEdit>
					)}
					<S.AccountEmail hide={isMobile ? false : footerWidth > 0 && footerWidth <= 600}>
						{authUser?.email || ''}
					</S.AccountEmail>
					<S.AccountLogout onClick={() => logout()}>Logout</S.AccountLogout>
				</S.AccountFooterContent>
			</S.AccountFooter>
		</S.Base>
	);
};

export default Profile;
